import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  formatInfraTypesLabel,
  getInfraFamily,
  getInfraTypesForRegion,
  infraByKey,
} from '../constants/infrastructureElementTypes';
import '../styles/ProjectSettings.css';

/**
 * Multi-select infrastructure element type picker (occupancy-style).
 */
export default function InfraTypeSelector({
  occupancyRegion = 'CA',
  selectedKeys = [],
  primaryKey = '',
  onChange,
  required = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);

  const region = occupancyRegion === 'US' ? 'US' : 'CA';
  const infraList = useMemo(() => getInfraTypesForRegion(region), [region]);

  const infraMap = useMemo(() => {
    const m = new Map();
    for (const [key, title, desc, code, family] of infraList) {
      m.set(key, { key, title, desc, code, family });
    }
    return m;
  }, [infraList]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return infraList;
    return infraList.filter(([key, title, desc, code]) =>
      title.toLowerCase().includes(q)
      || desc.toLowerCase().includes(q)
      || code.toLowerCase().includes(q)
      || key.toLowerCase().includes(q)
    );
  }, [infraList, search]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    setTimeout(() => document.addEventListener('click', handler, true), 0);
    return () => document.removeEventListener('click', handler, true);
  }, [open]);

  const toggleKey = (key, code) => {
    const isSelected = selectedKeys.includes(key);
    const nextKeys = isSelected ? selectedKeys.filter((k) => k !== key) : [...selectedKeys, key];
    const nextCodes = nextKeys.map((k) => infraMap.get(k)?.code).filter(Boolean);
    let nextPrimary = primaryKey;
    if (nextKeys.length === 1) nextPrimary = nextKeys[0];
    else if (!nextKeys.includes(primaryKey)) nextPrimary = nextKeys[0] || '';
    const family = nextPrimary ? getInfraFamily(nextPrimary) : '';
    onChange?.({
      infraTypes: nextKeys,
      infraTypeCodes: nextCodes.filter((c, i, arr) => arr.indexOf(c) === i),
      primaryInfraType: nextPrimary,
      infraFamily: family,
      infraType: nextPrimary ? (infraByKey(nextPrimary, region)?.title || nextPrimary) : '',
    });
  };

  const jurisdictionLabel = region === 'CA' ? 'Canada (Federal/Provincial)' : 'United States (IBC)';

  return (
    <div className="form-group" style={{ marginBottom: 0 }}>
      <label className="field-label">
        Element Types
        {required && <span className="required-star"> *</span>}
        <span className="optional"> (select all that apply)</span>
      </label>
      <div className="occ-wrapper" ref={wrapRef}>
        <div
          className="occ-display"
          onClick={() => { setSearch(''); setOpen((v) => !v); }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSearch(''); setOpen((v) => !v); } }}
        >
          {selectedKeys.length === 0 ? (
            <span className="occ-placeholder">Click to select element types…</span>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', flex: 1 }}>
              {selectedKeys.map((key) => (
                <span key={key} className="occ-tag">
                  {infraMap.get(key)?.title || key}
                  <span
                    className="x"
                    onClick={(e) => {
                      e.stopPropagation();
                      const item = infraMap.get(key);
                      if (item) toggleKey(item.key, item.code);
                    }}
                  >
                    ×
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className={`occ-popup${open ? ' show' : ''}`}>
          <div className="occ-popup-header">
            <div className="occ-popup-title">Select Element Types</div>
            <div className="occ-popup-subtitle">
              Showing: {jurisdictionLabel} · Select all that apply
            </div>
          </div>
          <div className="occ-search-wrap">
            <input
              type="text"
              placeholder="Search element types…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="occ-list">
            {filtered.map(([key, title, desc, code]) => {
              const checked = selectedKeys.includes(key);
              return (
                <div
                  key={key}
                  className={`occ-option${checked ? ' selected' : ''}`}
                  onClick={() => toggleKey(key, code)}
                >
                  <div className={`occ-checkbox${checked ? ' checked' : ''}`}>{checked ? '✓' : ''}</div>
                  <div>
                    <div className="occ-option-title">
                      {title}
                      <span className="occ-option-code">({code})</span>
                    </div>
                    <div className="occ-option-desc">{desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="occ-popup-footer">
            <span className="occ-count">{selectedKeys.length} selected</span>
            <button type="button" className="occ-done-btn" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      </div>
      <div className="field-hint">
        Showing: <strong>{jurisdictionLabel}</strong>. Click to open selector.
      </div>
      {selectedKeys.length > 1 && (
        <div className="form-group" style={{ marginTop: '0.75rem' }}>
          <label className="field-label">
            Primary Element Type<span className="required-star"> *</span>
          </label>
          <select
            value={primaryKey}
            onChange={(e) => {
              const pk = e.target.value;
              onChange?.({
                infraTypes: selectedKeys,
                infraTypeCodes: selectedKeys.map((k) => infraMap.get(k)?.code).filter(Boolean),
                primaryInfraType: pk,
                infraFamily: getInfraFamily(pk),
                infraType: pk ? (infraByKey(pk, region)?.title || pk) : '',
              });
            }}
          >
            <option value="">Select primary element type</option>
            {selectedKeys.map((key) => (
              <option key={key} value={key}>{infraMap.get(key)?.title || key}</option>
            ))}
          </select>
          <div className="field-hint">Select the dominant element type for metrics and compliance.</div>
        </div>
      )}
    </div>
  );
}

export { formatInfraTypesLabel };
